import React from "react"
const EDGE_EPSILON = 1;

const HINT_MS = 6000;

type ScrollGeometry = {
    scrollable: boolean
    moreLeft: boolean
    moreRight: boolean
    dots: number
    active: number
}

const AT_REST: ScrollGeometry = {
    scrollable: false,
    moreLeft: false,
    moreRight: false,
    dots: 0,
    active: 0,
};

export type ScrollHints = {
    ref(node: HTMLDivElement | null): void
    scrollable: boolean
    moreLeft: boolean
    moreRight: boolean
    dots: number
    active: number
    hint: boolean
}

function same(a: ScrollGeometry, b: ScrollGeometry): boolean {
    return a.scrollable === b.scrollable
        && a.moreLeft === b.moreLeft
        && a.moreRight === b.moreRight
        && a.dots === b.dots
        && a.active === b.active
}

/**
 * @param itemSelector matches the children that each earn a dot — separators
 *                     and the flexible spacer are deliberately left out of it
 */
export default function useScrollHints(itemSelector: string): ScrollHints {
    const [row, setRow] = React.useState<HTMLDivElement | null>(null);
    const [geometry, setGeometry] = React.useState<ScrollGeometry>(AT_REST);
    const [asked, setAsked] = React.useState(false);

    const measure = React.useCallback(() => {
        if (row === null) {
            setGeometry(previous => (same(previous, AT_REST) ? previous : AT_REST));

            return
        }

        const travel = row.scrollWidth - row.clientWidth;
        const left = row.scrollLeft;
        const box = row.getBoundingClientRect();
        const middle = box.left + (box.width / 2);
        const items = row.querySelectorAll(itemSelector);

        let active = 0;
        let nearest = Infinity;

        for (let index = 0; index < items.length; index++) {
            const rect = (items[index] as HTMLElement).getBoundingClientRect();
            const distance = Math.abs((rect.left + (rect.width / 2)) - middle);

            if (distance < nearest) {
                nearest = distance;
                active = index
            }
        }

        const moreLeft = left > EDGE_EPSILON;
        const moreRight = left < travel - EDGE_EPSILON;
        const next: ScrollGeometry = {
            scrollable: travel > EDGE_EPSILON,
            moreLeft: moreLeft,
            moreRight: moreRight,
            dots: items.length,
            active: !moreLeft ? 0 : (!moreRight ? Math.max(0, items.length - 1) : active),
        };

        setGeometry(previous => (same(previous, next) ? previous : next))
    }, [row, itemSelector]);
    React.useLayoutEffect(measure);

    React.useEffect(() => {
        if (row === null) {
            return undefined
        }

        function handleScroll() {
            setAsked(true);
            measure()
        }

        row.addEventListener("scroll", handleScroll, {passive: true});

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", measure);

            return () => {
                row.removeEventListener("scroll", handleScroll);
                window.removeEventListener("resize", measure)
            }
        }

        const observer = new ResizeObserver(() => measure());

        observer.observe(row);

        return () => {
            row.removeEventListener("scroll", handleScroll);
            observer.disconnect()
        }
    }, [row, measure]);

    React.useEffect(() => {
        if (asked || !geometry.scrollable) {
            return undefined
        }

        const timer = window.setTimeout(() => setAsked(true), HINT_MS);

        return () => window.clearTimeout(timer)
    }, [asked, geometry.scrollable]);

    return {
        ref: setRow,
        scrollable: geometry.scrollable,
        moreLeft: geometry.moreLeft,
        moreRight: geometry.moreRight,
        dots: geometry.dots,
        active: geometry.active,
        hint: geometry.scrollable && !asked,
    }
}
